import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * SyncStickyTableHead — 149-cj.
 *
 * Wide-table sticky header pengganti CSS-only. Root cause: Chrome me-normalisasi
 * `overflow-x: auto + overflow-y: clip` menjadi `overflow: auto hidden` (bug/spec
 * quirk), sehingga wrapper menjadi vertical scroll ancestor dan `position: sticky`
 * pada <thead> terjebak di dalam wrapper (tidak stick ke viewport).
 *
 * Solusi: portal-clone <thead> ke <body> sebagai floating fixed overlay yang:
 *   • posisi vertikal fixed (top: 96px kalau ada compact bar, 64px kalau tidak)
 *   • lebar & offset kiri mengikuti wrapper (via ResizeObserver + window.scroll)
 *   • scrollLeft sinkron dengan wrapper (via wrapper.onScroll → translateX)
 *   • hanya visible saat thead asli sudah scroll ke atas viewport
 *   • pointer-events: none — clone hanya visual, interaksi tetap pada thead asli
 *
 * Cara pakai:
 *   <SyncStickyTableHead offset="context" testid="my-table"
 *     className="bg-white border border-slate-200">
 *     <table className="w-full text-sm">
 *       <thead>...</thead>
 *       <tbody>...</tbody>
 *     </table>
 *   </SyncStickyTableHead>
 *
 * Prop offset:
 *   - "context" (default): top 96px — untuk page yang punya <StickyContextBar/>
 *   - "nav": top 64px — untuk page tanpa compact bar (mis. PerformancePage)
 */
export default function SyncStickyTableHead({
  children,
  offset = "context",
  className = "",
  testid,
}) {
  const wrapperRef = useRef(null);
  const topPx = offset === "nav" ? 64 : 96;

  const [state, setState] = useState({
    show: false,
    left: 0,
    width: 0,
    scrollLeft: 0,
    tableWidth: 0,
    version: 0,
  });
  // Clone disimpan sebagai DOM node (bukan string HTML) — aman dari injeksi markup.
  const cloneRef = useRef(null);
  const innerRef = useRef(null);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    let table = wrapper.querySelector(":scope > table") || wrapper.querySelector("table");
    if (!table) {
      // Table belum ter-render (loading state). Coba lagi via MutationObserver.
      const wait = new MutationObserver(() => {
        const t = wrapper.querySelector("table");
        if (t) { wait.disconnect(); attach(t); }
      });
      wait.observe(wrapper, { childList: true, subtree: true });
      return () => wait.disconnect();
    }
    return attach(table);

    function attach(tbl) {
      const getThead = () => tbl.tHead;

      const readSnapshot = () => {
        const thead = getThead();
        if (!thead || !thead.rows[0]) return;
        const rect = wrapper.getBoundingClientRect();
        const cells = Array.from(thead.rows[0].cells);
        const widths = cells.map((c) => c.getBoundingClientRect().width);
        // Strip inline sticky/pinning agar clone tidak double-sticky di dalam
        // container fixed. Bersihkan hanya properti position/left/right/z-index/boxShadow.
        const theadClone = thead.cloneNode(true);
        // 149-cw-r147 FIX (bug: judul tumpang tindih): Ketika kita hapus class
        // `ens-sticky-thead`, CSS `background: white` pada <th> ikut hilang →
        // clone <th> jadi TRANSPARENT → row asli di belakangnya "tembus"
        // sehingga terlihat overlap dgn header floating.
        //
        // Solusi: baca computed backgroundColor per <th> asli LEBIH DULU,
        // lalu inline ke clone <th> sebelum class-nya dihapus. Fallback:
        // background thead, lalu white. Ini juga aman untuk kasus accurate
        // header navy (bg-[#0B1D3A]) — computed bg akan tetap navy.
        try {
          const origCells = thead.querySelectorAll("th,td");
          const cloneCells = theadClone.querySelectorAll("th,td");
          const theadBg = window.getComputedStyle(thead).backgroundColor;
          const isTransparent = (v) => !v || v === "rgba(0, 0, 0, 0)" || v === "transparent";
          origCells.forEach((origEl, i) => {
            const clone = cloneCells[i];
            if (!clone) return;
            let bg = window.getComputedStyle(origEl).backgroundColor;
            if (isTransparent(bg)) bg = theadBg;
            if (isTransparent(bg)) bg = "#ffffff";
            clone.style.backgroundColor = bg;
          });
        } catch (_e) { /* getComputedStyle can fail on detached nodes — safe to ignore */ }
        // Hapus class `ens-sticky-thead*` supaya CSS bg-white pada <th> tidak
        // menimpa background asli (misal accurate `bg-[#0B1D3A]`). Aman karena
        // background sudah ter-inline per-cell di atas.
        theadClone.classList.remove("ens-sticky-thead", "ens-sticky-thead-nav");
        theadClone.querySelectorAll("th,td").forEach((el) => {
          el.style.position = "";
          el.style.left = "";
          el.style.right = "";
          el.style.zIndex = "";
          el.style.boxShadow = "";
        });
        // Susun clone <table> murni via DOM API (tanpa serialisasi HTML string).
        const tableWidth = tbl.getBoundingClientRect().width;
        const tableClone = document.createElement("table");
        tableClone.className = tbl.className;
        tableClone.style.tableLayout = "fixed";
        tableClone.style.width = `${tableWidth}px`;
        const colgroup = document.createElement("colgroup");
        widths.forEach((w) => {
          const col = document.createElement("col");
          col.style.width = `${w}px`;
          colgroup.appendChild(col);
        });
        tableClone.appendChild(colgroup);
        tableClone.appendChild(theadClone);
        cloneRef.current = tableClone;
        setState((s) => ({
          ...s,
          left: rect.left,
          width: rect.width,
          scrollLeft: wrapper.scrollLeft,
          tableWidth,
          version: s.version + 1,
        }));
      };

      const updateShow = () => {
        const thead = getThead();
        if (!thead) return;
        const wrapRect = wrapper.getBoundingClientRect();
        const theadRect = thead.getBoundingClientRect();
        // Show ketika thead asli sudah scroll ke atas offset AND wrapper masih
        // menjangkau area viewport di bawah offset.
        const show = theadRect.top < topPx - 2 && wrapRect.bottom > topPx + 8;
        setState((s) => (s.show === show ? s : { ...s, show }));
      };

      const syncPosition = () => {
        const rect = wrapper.getBoundingClientRect();
        setState((s) => ({
          ...s,
          left: rect.left,
          width: rect.width,
          scrollLeft: wrapper.scrollLeft,
        }));
      };

      readSnapshot();
      updateShow();

      const ro = new ResizeObserver(() => { readSnapshot(); updateShow(); });
      ro.observe(wrapper);
      ro.observe(tbl);

      const onScroll = () => { updateShow(); syncPosition(); };
      const onResize = () => { readSnapshot(); updateShow(); };
      const onWrapScroll = () => syncPosition();
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onResize);
      wrapper.addEventListener("scroll", onWrapScroll, { passive: true });

      const mo = new MutationObserver(() => readSnapshot());
      const thead0 = getThead();
      if (thead0) mo.observe(thead0, { childList: true, subtree: true, characterData: true, attributes: true });

      return () => {
        ro.disconnect();
        mo.disconnect();
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onResize);
        wrapper.removeEventListener("scroll", onWrapScroll);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topPx]);

  // Pasang clone DOM ke portal — replaceChildren memindahkan node yang sama,
  // tanpa parsing string HTML (pengganti dangerouslySetInnerHTML).
  useLayoutEffect(() => {
    if (state.show && innerRef.current && cloneRef.current) {
      innerRef.current.replaceChildren(cloneRef.current);
    }
  }, [state.show, state.version]);

  return (
    <>
      <div
        ref={wrapperRef}
        className={`ens-table-scroll ${className}`}
        data-testid={testid}
      >
        {children}
      </div>
      {state.show && typeof document !== "undefined" && createPortal(
        <div
          aria-hidden="true"
          data-testid={testid ? `${testid}-sync-head` : "sync-sticky-head"}
          style={{
            position: "fixed",
            top: topPx,
            left: state.left,
            width: state.width,
            overflow: "hidden",
            zIndex: 22,
            pointerEvents: "none",
            background: "transparent",
          }}
        >
          <div
            ref={innerRef}
            style={{
              transform: `translateX(-${state.scrollLeft}px)`,
              willChange: "transform",
              width: state.tableWidth || "auto",
            }}
          />
        </div>,
        document.body
      )}
    </>
  );
}
